import React, {
  ReactElement,
  Fragment,
  SetStateAction,
  useState,
  useEffect,
} from "react";
import { Translate } from "react-redux-i18n";
import {
  Box,
  TableCell,
  TableRow,
  Typography
} from "@mui/material";
import { format } from "date-fns";

import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Revenues, RevenuesData, getRevenueDetailsRequested, getRevenuesChildrenRequested } from "redux/wallet/walletSlice";
import { openDialog } from "redux/dialog/dialogSlice";
import { RevenueDetailFilter, RevenueFilterElement } from "../constants";
import styles from "./styles.module.scss";

interface IProps {
  revenue: Revenues;
  table?: string;
  preset?: string;
  filters: RevenueFilterElement;
  padding: number;
  setPadding: React.Dispatch<SetStateAction<number>>
}

export default function RevenueRow({
  revenue,
  filters,
  padding,
  setPadding
}: IProps): ReactElement {
  const dispatch = useAppDispatch()
  const revenuesChildren = useAppSelector(state => state.wallet.revenuesChildren)
  const [count, setCount] = useState(padding)
  const [isExpandClicked, setIsExpandedClicked] = useState<boolean>(false)
  const [isWalletOpen, setIsWalletOpen] = useState<boolean>(false)

  const handleExpand = () => {
    if (!isWalletOpen && revenue.wallet_type.children === true) {
      const copy = { ...filters };
      delete copy["wallet_id"];
      dispatch(
        getRevenuesChildrenRequested({
          id: revenue.id,
          params: {
            filters: { ...copy, children: true },
          },
        }))
    }
    setIsWalletOpen(!isWalletOpen)
    !isExpandClicked && setCount(padding + 2);
    setIsExpandedClicked(true)
  }

  const handleInfoClick = (type: string, data: Revenues) => {
    dispatch(openDialog({ type: type, payload: data }))
  }

  return (
    <Fragment>
      <TableRow className={styles.tableRow}>
        <TableCell
          sx={{ pl: padding }}
          className={[styles.tableCell, styles.tableCellExpand].join(" ")}
        >
          <Box className={styles.usernameContainer}>
            <Box
              onClick={handleExpand}
              className={styles.userBox}
            >
              {revenue.wallet_type.children &&
                <Box component="span" className={styles.expandIcon} >
                  <FontAwesomeIcon name={`chevron-${isWalletOpen ? "down" : "right"}`} type="fas" />
                </Box>
              }
              <Box className={styles.user}>
                {revenue.wallet_type.icon &&
                  <Box>
                    <FontAwesomeIcon name={revenue.wallet_type.icon} type="fas" />
                  </Box>}
                <Box component="span" className={styles.userName}>{revenue.name}</Box>
              </Box>
            </Box>

          </Box>
        </TableCell>
        <RevenueTable handleInfoClick={handleInfoClick} revenue={revenue} filters={filters} />
      </TableRow>
      {revenue &&
        isWalletOpen &&
        revenuesChildren &&
        revenuesChildren[revenue.id]?.map((child: Revenues) =>
          <RevenueRow
            key={child.id}
            revenue={child}
            filters={filters}
            padding={count}
            setPadding={setPadding}
          />
        )}
    </Fragment>
  )
}

interface RevenueTableProps {
  revenue: Revenues
  handleInfoClick: (type: string, data: Revenues) => void
  filters: RevenueDetailFilter
}

function RevenueTable({ revenue, handleInfoClick, filters }: RevenueTableProps): ReactElement {
  const dispatch = useAppDispatch()
  const isDesktopLayout = useAppSelector(state => state.wallet.isDesktopLayout)
  const divider = useAppSelector(state => state.wallet.revenues?.currency.divider)
  const decimal = useAppSelector(state => state.wallet.revenues?.currency.decimal)
  const currency = useAppSelector(state => state.wallet.revenues?.currency.symbol)

  const handleIconClick = () => {
    const detailFilters = {
      commercial_end_date: filters.commercial_end_date,
      commercial_start_date: filters.commercial_start_date,
      context: filters.context,
      date_type: filters.date_type,
      manual_end_date: filters.manual_end_date,
      manual_start_date: filters.manual_start_date
    }
    dispatch(getRevenueDetailsRequested({ id: revenue.id, params: detailFilters, wallet: revenue }))
  }
  return (
    <>
      {
        revenue.revenues.map((el: RevenuesData) => {
          return (
            <>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.play_amount / divider).toFixed(decimal)} {currency}
              </TableCell>

              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.play_amount_closed / divider).toFixed(decimal)}{currency}&nbsp;({el.records_closed})
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.win_amount_closed / divider).toFixed(decimal)} {currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} table-cell`}>
                <Typography className={`${styles.profit} 
                ${el.profit_amount > 0 ? styles.positiveProfit
                    : el.profit_amount < 0 ? styles.negativeProfit
                      : ""}`}
                >
                  {divider && (el.profit_amount / divider).toFixed(decimal)} {currency}
                </Typography>
                {revenue.revenues[0].context.detail &&
                  <span onClick={() => handleIconClick()} className={styles.iconContainer}>
                    <FontAwesomeIcon name="magnifying-glass" type="fas" />
                  </span>
                }
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.play_amount_average / divider).toFixed(decimal)} {currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.children_turnover_amount / divider).toFixed(decimal)} {currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.children_bonus_amount / divider).toFixed(decimal)} {currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.children_profit_amount / divider).toFixed(decimal)} {currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {divider && (el.bonus_amount / divider).toFixed(decimal)} {currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {
                  divider && revenue.wallet_type.name === "agency" ? (el.children_profit_amount / divider).toFixed(decimal) :
                    divider && ((el.children_profit_amount - el.bonus_amount) / divider).toFixed(decimal)
                }
                &nbsp;{currency}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellProvigionalMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.tableCellProvigional} ${styles.removeMobile}`}>
                {revenue.provisional_plan.name}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {el.type}
              </TableCell>
              <TableCell size="small" className={isDesktopLayout ? styles.tableCellMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeMobile}`}>
                {format(new Date(el.updated_at), `${process.env.REACT_APP_DATE_TIME_FORMAT}`)}
              </TableCell>
              <TableCell onClick={() => handleInfoClick("revenue", revenue)} size="small" className={isDesktopLayout ? styles.tableCellRemoveMobileForDesktop : `${styles.tableCell} ${styles.tableCellSmall} ${styles.removeDesktop}`}>
                <FontAwesomeIcon name="circle-info" type="fas" />
              </TableCell>
              {/* <TableCell size="small" className={[styles.tableCell, styles.tableCellSmall, styles.removeDesktop, "table-cell"].join(" ")}>
                <FontAwesomeIcon name="circle-info" type="fas" />
              </TableCell> */}
            </>
          )
        })
      }
    </>
  )
}

interface RevenueMobile {
  revenue: Revenues
}

export function RevenuesMobile({ revenue }: RevenueMobile): ReactElement {
  const divider = useAppSelector(state => state.wallet.revenues?.currency.divider)
  const decimal = useAppSelector(state => state.wallet.revenues?.currency.decimal)
  const currency = useAppSelector(state => state.wallet.revenues?.currency.symbol)
  const [mobileData, setMobileData] = useState<any | undefined>(undefined)
  const sortedKeys = ["play_amount", "play_amount_closed", "win_amount_closed", "profit_amount", "play_amount_average", "children_turnover_amount", "children_bonus_amount", "children_profit_amount", "bonus_amount", "netProfit", " provisional_plan", "type", "updated_at"]
  useEffect(() => {
    const mapObject = new Map();
    let newObject: any = {}
    sortedKeys.forEach(key => {
      mapObject.set(key, [])
    })
    newObject = Object.fromEntries(mapObject)
    Object.keys(newObject).map(key => {
      revenue.revenues.forEach((el: any) => {
        newObject[key].push(el[key])
      })
    })
    setMobileData(newObject)
  }, [revenue])

  console.log(revenue)

  return (
    <Box className={styles.revenueMobileContainer}>
      {
        mobileData && Object.keys(mobileData).map(key => {
          return (
            <Box key={key} className={styles.revenueSingleRow}>
              <Typography className={styles.mobileRowTitle}><Translate value={`revenue.${key}`} /></Typography>
              <Box className={styles.revenueSingleRowData}>
                {
                  mobileData[key].map((el: any) => {
                    return (
                      <Box key={el}>
                        {key === "play_amount_closed" ?
                          <Typography className={styles.singleRowData}>
                            {divider && (revenue.revenues[0].play_amount_closed / divider).toFixed(decimal)}{currency}&nbsp; ({revenue.revenues[0].records_closed})
                          </Typography>
                          : key === " provisional_plan" ? <Typography className={styles.singleRowData}>{revenue.provisional_plan.name}
                          </Typography> :
                            key === "netProfit" ?
                              <Typography className={styles.singleRowData}>
                                {
                                  divider && revenue.wallet_type.name === "agency" ? ((revenue.revenues[0].children_profit_amount) / divider).toFixed(decimal) :
                                    divider && ((revenue.revenues[0].children_profit_amount - revenue.revenues[0].bonus_amount) / divider).toFixed(decimal)
                                }
                                &nbsp;{currency}
                              </Typography>
                              :
                              key === "updated_at" ?
                                <Typography className={styles.singleRowData}>
                                  {format(new Date(revenue.revenues[0].updated_at), `${process.env.REACT_APP_DATE_TIME_FORMAT}`)}
                                </Typography>
                                :
                                <Typography className={styles.singleRowData}>{Number(el) ? divider && (el / divider).toFixed(decimal) : el}&nbsp;{Number(el) ? currency : ""}</Typography>
                        }
                      </Box>
                    )
                  }
                  )
                }
              </Box>
            </Box>
          )
        })
      }
    </Box>
  )
}